import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './LoginForm.css'

function RegisterForm() {
  const [formData, setFormData] = useState({
    email: '',
    temporaryPassword: '',
    firstName: '',
    lastName: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState(false)

  const { inviteUser, error, clearError } = useAuth()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear errors when user starts typing
    if (localError) setLocalError('')
    if (error) clearError()
  }

  const validateForm = () => {
    if (!formData.email || !formData.temporaryPassword || !formData.firstName || !formData.lastName) {
      return 'Please fill in all fields'
    }

    if (!formData.email.includes('@')) {
      return 'Please enter a valid email address'
    }

    if (formData.temporaryPassword.length < 8) {
      return 'Temporary password must be at least 8 characters long'
    }

    // Check password requirements
    const hasUpperCase = /[A-Z]/.test(formData.temporaryPassword)
    const hasLowerCase = /[a-z]/.test(formData.temporaryPassword)
    const hasNumbers = /\d/.test(formData.temporaryPassword)

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      return 'Temporary password must contain uppercase, lowercase, and numbers'
    }

    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setLocalError('')

    const validationError = validateForm()
    if (validationError) {
      setLocalError(validationError)
      setIsLoading(false)
      return
    }

    try {
      const result = await inviteUser(
        formData.email, 
        formData.firstName, 
        formData.lastName,
        formData.temporaryPassword
      )
      
      if (result.success) {
        setInviteSuccess(true)
        setFormData({
          email: '',
          temporaryPassword: '',
          firstName: '',
          lastName: ''
        })
      } else {
        setLocalError(result.error || 'Failed to invite user')
      }
    } catch {
      setLocalError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const displayError = localError || error

  if (inviteSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invitation Sent!</h1>
            <p>The new member has been successfully invited to join the club</p>
          </div>

          <div className="success-message">
            <p>
              An email with login instructions and a temporary password has been sent to the new member. 
              They will be prompted to change their password on first login.
            </p>
          </div>

          <div className="auth-links">
            <button 
              type="button" 
              onClick={() => {
                setInviteSuccess(false)
                setLocalError('')
              }}
              className="auth-button secondary"
            >
              Invite Another Member
            </button>
            <Link to="/admin" className="auth-button primary">
              Return to Admin Panel
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Invite New Member</h1>
          <p>Add a new member to Wingbeat Paragliding Club</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {displayError && (
            <div className="error-message">
              {displayError}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                required
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="temporaryPassword">Temporary Password</label>
            <input
              type="password"
              id="temporaryPassword"
              name="temporaryPassword"
              value={formData.temporaryPassword}
              onChange={handleChange}
              placeholder="Create a temporary password"
              required
              autoComplete="new-password"
            />
            <div className="password-requirements">
              <small>Must be 8+ characters with uppercase, lowercase, and numbers. User will change this on first login.</small>
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Sending Invitation...' : 'Send Invitation'}
          </button>
        </form>

        <div className="auth-links">
          <div className="auth-divider">
            <span>Return to admin panel</span>
          </div>
          <Link to="/admin" className="auth-button secondary">
            Admin Panel
          </Link>
        </div>
      </div>
    </div>
  )
}

export default RegisterForm